import { colors } from '@/theme/colors';
import type {
  NewProjectRequestNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { Inline } from '@/ui/line/line';
import { SmallText } from '@/ui/typography/widgets';
import { Trans, useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';

import { Icon } from '@/ui/icons/icon';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import {
  NotificationCardContainer,
  NotificationHighlight,
  NotificationIconWrapper,
} from '../components/notification-card-components';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { isPendingRequestsViewOpenState } from '../notifications.state';
import { timeAgo } from '../utils/time-ago';

export const NewProjectRequestNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { requesterEmail } =
    notification.data as NewProjectRequestNotificationData;
  const isRead = Boolean(notification?.readAt);
  const { markAsRead } = useMarkNotificationAsRead();
  const setPendingRequestsViewOpen = useSetAtom(isPendingRequestsViewOpenState);
  const { hasRequiredPermission } = useUserPermissions();
  const canViewPendingRequests = hasRequiredPermission(
    PERMISSION.VIEW_PENDING_PROJECT_REQUESTS,
  );

  const handleNotificationClick = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    if (canViewPendingRequests) {
      setPendingRequestsViewOpen(true);
    }
  };

  return (
    <NotificationCardContainer
      isRead={isRead}
      clickable={canViewPendingRequests}
      justify="space-between"
      alignItems="center"
      onClick={handleNotificationClick}
    >
      <Inline gap="12px" alignItems="center">
        <NotificationIconWrapper
          isRead={isRead}
          unreadColor={colors.system.lowFit}
          alignItems="center"
          justify="center"
        >
          <Icon icon="Clock" color="white" />
        </NotificationIconWrapper>
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="newProjectRequestTitle"
            ns="notification"
            values={{ requesterEmail }}
            components={{ strong: <NotificationHighlight /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </NotificationCardContainer>
  );
};
